import React from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { Button } from "../ui/button";

/**
 * Compact multi-select "chips" trigger that opens a popover with a
 * checkbox list. Used for status filters on queue pages.
 *
 *   selected: string[] of currently-checked values
 *   options: [{value, label}]
 */
export function MultiSelectFilter({
  selected,
  options,
  onChange,
  triggerLabel,
  testIdRoot,
}) {
  const { t } = useTranslation();
  const summary =
    selected.length === 0
      ? triggerLabel
      : selected.length === 1
      ? options.find((o) => o.value === selected[0])?.label || triggerLabel
      : `${triggerLabel} · ${selected.length}`;

  const toggle = (v) => {
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else onChange([...selected, v]);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-testid={`${testIdRoot}-trigger`}
          className="h-10 border-border-subtle bg-surface text-sm text-text-primary hover:bg-offwhite"
        >
          {summary}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 border-border-subtle bg-surface p-2"
        data-testid={`${testIdRoot}-popover`}
      >
        <ul className="space-y-1">
          {options.map((o) => {
            const checked = selected.includes(o.value);
            return (
              <li key={o.value}>
                <button
                  type="button"
                  onClick={() => toggle(o.value)}
                  data-testid={`${testIdRoot}-option-${o.value}`}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-offwhite"
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      checked
                        ? "border-champagne bg-champagne text-charcoal"
                        : "border-border-subtle bg-surface"
                    }`}
                  >
                    {checked && <Check className="h-3 w-3" />}
                  </span>
                  <span className="truncate">{o.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            data-testid={`${testIdRoot}-clear`}
            className="mt-2 w-full rounded-md px-2 py-1.5 text-xs text-text-secondary hover:bg-offwhite"
          >
            {t("common.clear_filters")}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
